// Token Service - Token management utilities
//
// Handles:
// - JWT token decoding
// - Token expiry checking
// - Token refresh logic
// - Token storage management

import API from '@config/shared/api-paths';
import { notifySessionChange } from '@utils/shared/session-storage-sync';
import { handleRefreshForbidden } from './no-access';

const TOKEN_REFRESH_THRESHOLD = 60; // Refresh token if it expires in less than 60 seconds

/**
 * Decode JWT token
 */
export const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.codePointAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.debug('Token decode failed:', e);
    return null;
  }
};

/**
 * Check if token is expired or about to expire
 */
export const isTokenExpiringSoon = (token) => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded?.exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = decoded.exp - now;
  
  return expiresIn < TOKEN_REFRESH_THRESHOLD;
};

/**
 * Refresh access token
 */
export const refreshAccessToken = async () => {
  const refreshToken = sessionStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${API.BASE}${API.AUTH.REFRESH}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${refreshToken}`,
    },
  });

  // 403 = access withdrawn: session ended + redirected to the No Access page.
  if (await handleRefreshForbidden(response)) {
    const error = new Error('No access');
    error.noAccess = true;
    throw error;
  }

  if (!response.ok) {
    // Clear tokens on refresh failure
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('features');
    sessionStorage.removeItem('user');
    throw new Error('Token refresh failed');
  }

  const data = await response.json();

  // Store new tokens
  sessionStorage.setItem('accessToken', data.access);
  sessionStorage.setItem('refreshToken', data.refresh);

  // The refresh endpoint ROTATES the refresh token, so the pair we just stored
  // invalidates the one every other tab still holds. Broadcast the new pair so
  // sibling tabs adopt it — otherwise the next tab to refresh does so with a
  // now-stale token, gets a 401, and cascades a logout across all tabs.
  notifySessionChange();

  return data.access;
};

/**
 * Clear all authentication tokens and data
 */
export const clearAuthData = () => {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('features');
  sessionStorage.removeItem('user');
};
